import React, { useMemo } from 'react';
import { useAuth } from '@/legacy/redux';
import { storeScope } from '@/redux/scope';
import { CommandList } from '../components/CommandList';
import { useRegisterCommands, useRegisterPage, usePluginNavigation } from '../hooks';
import type { Command as CommandType } from '../types';
import { getActiveTenancy, requestTenancySelection, setActiveTenancy } from '@/features/tenancy';

const PLUGIN_ID = 'tenancy-plugin';
const TENANCY_PAGE = 'tenancy';
const TENANCY_PLACEHOLDER = 'Select tenancy...';

// ---------------------------------------------------------------------------
// TenancyPage — lists available tenancies; selecting one switches and reloads
// ---------------------------------------------------------------------------

const TenancyPage: React.FC = () => {
  const { user } = useAuth();
  const currentTenant = getActiveTenancy();

  const commands = useMemo((): CommandType[] => {
    const groups: { id: string; name: string }[] = user?.groups ?? [];
    return groups.map(group => ({
      id: `tenancy-${group.id}`,
      label: group.name,
      description: group.id === currentTenant ? 'Current tenancy' : undefined,
      category: 'Tenancy',
      keywords: [group.name, group.id],
      primary: {
        id: `tenancy-${group.id}-select`,
        label: 'Switch to this tenancy',
        action: () => {
          if (!setActiveTenancy(group.id)) {
            requestTenancySelection();
            return;
          }
          storeScope({});
          window.location.reload();
        },
      },
    }));
  }, [user?.groups, currentTenant]);

  return <CommandList commands={commands} emptyMessage="No tenancies available." />;
};

// ---------------------------------------------------------------------------
// TenancyPlugin — registers the global command and page
// ---------------------------------------------------------------------------

export const TenancyPlugin: React.FC = () => {
  const { navigateTo } = usePluginNavigation(PLUGIN_ID);

  const globalCommands = useMemo((): CommandType[] => [
    {
      id: 'change-tenancy',
      label: 'Change Tenancy',
      description: 'Switch to a different tenancy',
      category: 'System',
      keywords: ['tenancy', 'tenant', 'switch', 'organisation', 'organization'],
      primary: {
        id: 'change-tenancy-action',
        label: 'Change Tenancy',
        action: () => navigateTo(TENANCY_PAGE, TENANCY_PLACEHOLDER),
      },
    },
  ], [navigateTo]);

  useRegisterCommands({ pluginId: PLUGIN_ID, commands: globalCommands });
  useRegisterPage({ pluginId: PLUGIN_ID, pageId: TENANCY_PAGE, component: TenancyPage });

  return null;
};
